import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AtBatResultBanner from '../components/AtBatResultBanner';
import FieldDiamond from '../components/FieldDiamond';
import InningDetailModal, {
  type InningModalTarget,
} from '../components/InningDetailModal';
import LineScore from '../components/LineScore';
import Lineup from '../components/Lineup';
import PitcherChip from '../components/PitcherChip';
import PitchLog from '../components/PitchLog';
import PlayerModal, { type PlayerSeed } from '../components/PlayerModal';
import Ribbon from '../components/Ribbon';
import SideNav from '../components/SideNav';
import TopBar from '../components/TopBar';
import VenueBadge from '../components/VenueBadge';
import { useDebouncedError } from '../hooks/useDebouncedError';
import { usePrefetchPlayers } from '../api/usePrefetchPlayers';
import { gameModeFromSearch, useGameStream } from '../hooks/useGameStream';
import { useEffect, useMemo, useRef } from 'react';
import {
  selectAllRosteredIds,
  selectAtBatContext,
  selectBatterOnField,
  selectClickablePlayerIds,
  selectCurrentAtBatIndex,
  selectCurrentAtBatResult,
  selectCurrentPitcher,
  selectFielders,
  selectLatestAtBatIndex,
  selectLineScore,
  selectLineup,
  selectMostRecentPitcherIds,
  selectPlayByAtBatIndex,
  selectRibbon,
  selectRunners,
  selectVenueInfo,
} from '../state/selectors';
import styles from './GameView.module.css';

export default function GameView(): JSX.Element {
  const { gamePk } = useParams();
  const [searchParams] = useSearchParams();
  const gamePkNum = gamePk ? Number(gamePk) : undefined;
  const mode = gameModeFromSearch(searchParams);
  const { snapshot, status, lastError, statcast } = useGameStream(gamePkNum, { mode });
  const { visible: visibleError, dismiss: dismissError } = useDebouncedError(lastError);

  const ribbon = selectRibbon(snapshot);
  const runners = selectRunners(snapshot);
  const fielders = selectFielders(snapshot);
  const awayLineup = selectLineup(snapshot, 'away');
  const homeLineup = selectLineup(snapshot, 'home');
  const pitcher = selectCurrentPitcher(snapshot);
  const lineScore = selectLineScore(snapshot);
  const venue = selectVenueInfo(snapshot);
  const batterOnField = selectBatterOnField(snapshot);
  const atBatResult = selectCurrentAtBatResult(snapshot);

  // At-bat navigation state.
  // - `null` = follow the latest at-bat (auto-tracks when MLB advances).
  // - a concrete number = the user explicitly stepped back; stay put until
  //   they hit "Latest" or pick another batter.
  const [selectedAtBatIndex, setSelectedAtBatIndex] = useState<number | null>(null);
  const latestAtBatIndex = useMemo(() => selectLatestAtBatIndex(snapshot), [snapshot]);
  const effectiveAtBatIndex = selectedAtBatIndex ?? latestAtBatIndex;
  const atBatContext = useMemo(
    () => selectAtBatContext(snapshot, effectiveAtBatIndex, statcast?.pitches),
    [snapshot, effectiveAtBatIndex, statcast?.pitches],
  );
  const isLatest = selectedAtBatIndex === null;
  const canStepBack =
    effectiveAtBatIndex != null && effectiveAtBatIndex > 0;
  const canStepForward =
    !isLatest && effectiveAtBatIndex != null && latestAtBatIndex != null &&
    effectiveAtBatIndex < latestAtBatIndex;
  // Warm the player-stats cache for the FULL boxscore roster — not just
  // currently-on-field clickables. Covers bench + bullpen so clicks from the
  // Rosters page (and any future surface) feel instant. The "clickable" set
  // is included so it's prefetched first when present (insertion order is
  // preserved in the Set).
  const prefetchIds = useMemo(() => {
    const clickable = selectClickablePlayerIds(snapshot);
    const all = selectAllRosteredIds(snapshot);
    const seen = new Set<number>();
    for (const id of clickable) seen.add(id);
    for (const id of all) seen.add(id);
    return Array.from(seen);
  }, [snapshot]);
  usePrefetchPlayers(gamePkNum, prefetchIds);

  // Pitching-change banner: track the previous pitcher id per side; when it
  // changes, set a transient incomingPitcher with a bumped triggerKey so the
  // PitchingChangeBanner remounts its fade timer.
  const prevPitcherIds = useRef<{ away: number | null; home: number | null }>({
    away: null,
    home: null,
  });
  const triggerKey = useRef(0);
  const [incomingPitcher, setIncomingPitcher] = useState<
    { name: string; jersey: string | null; key: number } | null
  >(null);
  useEffect(() => {
    if (!snapshot) return;
    const { away, home } = selectMostRecentPitcherIds(snapshot);
    const prev = prevPitcherIds.current;
    let changedId: number | null = null;
    let changedSide: 'away' | 'home' | null = null;
    if (away !== prev.away && away != null) {
      changedId = away;
      changedSide = 'away';
    } else if (home !== prev.home && home != null) {
      changedId = home;
      changedSide = 'home';
    }
    // First snapshot — just record, don't banner.
    const isFirstSnap = prev.away === null && prev.home === null;
    prevPitcherIds.current = { away, home };
    if (!isFirstSnap && changedId != null && changedSide) {
      // Resolve the new pitcher's name + jersey from boxscore.
      const liveData = (snapshot as Record<string, unknown>).liveData as Record<string, unknown> | undefined;
      const boxscore = (liveData?.boxscore ?? {}) as Record<string, unknown>;
      const teams = (boxscore.teams ?? {}) as Record<string, unknown>;
      const players = ((teams[changedSide] as Record<string, unknown>)?.players ?? {}) as Record<string, unknown>;
      const playerEntry = (players[`ID${changedId}`] ?? {}) as Record<string, unknown>;
      const person = (playerEntry.person ?? {}) as Record<string, unknown>;
      const name = typeof person.fullName === 'string' ? person.fullName : `Player ${changedId}`;
      const jersey = typeof playerEntry.jerseyNumber === 'string' ? playerEntry.jerseyNumber : null;
      triggerKey.current += 1;
      setIncomingPitcher({ name, jersey, key: triggerKey.current });
    }
  }, [snapshot]);

  const [selectedPlayer, setSelectedPlayer] = useState<PlayerSeed | null>(null);
  const [inningTarget, setInningTarget] = useState<InningModalTarget | null>(null);
  // All click sites now pass a {id, name, jersey, position} seed so the modal
  // heading paints synchronously from props — no skeleton flash on first paint.
  const onPlayerClickSeeded = (seed: PlayerSeed): void => setSelectedPlayer(seed);
  const closeModal = (): void => setSelectedPlayer(null);
  const closeInningModal = (): void => setInningTarget(null);

  // At-bat result banner state — driven by selectAtBatContext for the currently
  // displayed at-bat. The banner + arrows fade after 8s; user can X-dismiss
  // early; the always-on Replay pill brings them back by bumping replayCounter.
  const lastAtBatResult = useMemo(
    () =>
      atBatContext && atBatContext.isComplete && atBatContext.event
        ? {
            atBatIndex: atBatContext.atBatIndex,
            event: atBatContext.event,
            description: atBatContext.description,
            runners: atBatContext.runners,
          }
        : null,
    [atBatContext],
  );
  const [dismissedAtBatIndex, setDismissedAtBatIndex] = useState<number | null>(null);
  const [replayCounter, setReplayCounter] = useState<number>(0);
  // When the user navigates to a different at-bat, drop any prior dismissal so
  // the new result fires its full 8s timer.
  useEffect(() => {
    setDismissedAtBatIndex(null);
  }, [effectiveAtBatIndex]);
  const handleDismissResult = (atBatIndex: number): void => {
    setDismissedAtBatIndex(atBatIndex);
  };
  const handleReplayResult = (): void => {
    setDismissedAtBatIndex(null);
    setReplayCounter((c) => c + 1);
  };

  function handleInningCellClick(inning: number, half: 'top' | 'bottom'): void {
    setInningTarget({ inning, half });
  }

  function handleResultClick(): void {
    const atBatIdx = selectCurrentAtBatIndex(snapshot);
    if (atBatIdx == null) return;
    const target = selectPlayByAtBatIndex(snapshot, atBatIdx);
    if (!target) return;
    setInningTarget({ inning: target.inning, half: target.half, atBatIndex: atBatIdx });
  }

  return (
    <>
      <TopBar />
      <SideNav gamePk={gamePkNum} mode={mode} />
      <main aria-labelledby="game-view-heading" className={styles.page}>
        <h1 id="game-view-heading" className={styles.heading}>Game {gamePk}</h1>
        {status === 'connecting' && !snapshot && (
          <p data-testid="connecting" className={styles.connecting}>Connecting…</p>
        )}
        {ribbon && <Ribbon state={ribbon} />}
        {mode === 'live' ? (
          <div className={styles.liveBadge} data-testid="live-badge">
            <span className={styles.liveDot} aria-hidden="true" />
            LIVE
          </div>
        ) : (
          <div className={styles.historicalBadge} data-testid="historical-badge">
            HISTORICAL
          </div>
        )}
        {visibleError && (
          <div role="alert" data-testid="degraded" className={styles.degraded}>
            <span className={styles.degradedText}>
              Degraded stream ({visibleError.code}): {visibleError.message}
            </span>
            <button
              type="button"
              onClick={dismissError}
              aria-label="Dismiss error"
              data-testid="degraded-dismiss"
              className={styles.degradedDismiss}
            >
              ×
            </button>
          </div>
        )}
        {venue && <VenueBadge venue={venue} />}
        {/* Live data first: the box/line score and pitcher/batter/pitch
         * summary lead the page so in-progress game state is visible without
         * scrolling; the lineups + field diamond follow as supporting context.
         * (Comment 126 — Patrick.) */}
        {ribbon && (
          <section className={styles.summaryGrid} data-testid="game-view-summary-grid">
            <div className={styles.lineScoreColumn}>
              {pitcher && (
                <PitcherChip
                  pitcher={pitcher}
                  awayTeamCode={ribbon.awayTeamCode}
                  homeTeamCode={ribbon.homeTeamCode}
                  onClick={onPlayerClickSeeded}
                />
              )}
              {lineScore && (
                <LineScore
                  state={lineScore}
                  awayTeamCode={ribbon.awayTeamCode}
                  homeTeamCode={ribbon.homeTeamCode}
                  onInningClick={handleInningCellClick}
                  gamePk={gamePkNum}
                  currentInning={ribbon.inning}
                  currentHalf={ribbon.isTopInning ? 'top' : 'bottom'}
                  mode={mode}
                />
              )}
            </div>
            <PitchLog
              pitches={atBatContext?.pitches ?? []}
              onResultClick={handleResultClick}
              atBatResult={
                isLatest
                  ? atBatResult
                  : atBatContext && atBatContext.isComplete && atBatContext.event
                    ? { event: atBatContext.event, description: atBatContext.description }
                    : null
              }
              batter={
                atBatContext?.batter ??
                (batterOnField
                  ? { id: batterOnField.id, name: batterOnField.name, jersey: batterOnField.jersey }
                  : null)
              }
              pitcher={
                atBatContext?.pitcher ??
                (pitcher
                  ? { id: pitcher.id, name: pitcher.name, jersey: pitcher.jersey }
                  : null)
              }
              onPlayerClick={onPlayerClickSeeded}
              onPrev={
                canStepBack
                  ? () => setSelectedAtBatIndex((effectiveAtBatIndex ?? 0) - 1)
                  : undefined
              }
              onNext={
                canStepForward
                  ? () => setSelectedAtBatIndex((effectiveAtBatIndex ?? 0) + 1)
                  : undefined
              }
              onLatest={isLatest ? undefined : () => setSelectedAtBatIndex(null)}
              isLatest={isLatest}
              canPrev={canStepBack}
              canNext={canStepForward}
            />
          </section>
        )}
        {ribbon && (
          <section className={styles.fieldGrid} data-testid="game-view-grid">
            <Lineup
              side="away"
              teamCode={ribbon.awayTeamCode}
              teamAbbr={ribbon.awayAbbr}
              hitters={awayLineup}
              onPlayerClick={onPlayerClickSeeded}
            />
            <div className={styles.center}>
              <div className={styles.resultBannerSlot}>
                {lastAtBatResult && (
                  <AtBatResultBanner
                    key={`${lastAtBatResult.atBatIndex}-${replayCounter}`}
                    result={lastAtBatResult}
                    dismissedAtBatIndex={dismissedAtBatIndex}
                    replayKey={replayCounter}
                    onDismiss={handleDismissResult}
                  />
                )}
              </div>
              <FieldDiamond
                fielders={fielders}
                runners={runners}
                awayTeamCode={ribbon.awayTeamCode}
                homeTeamCode={ribbon.homeTeamCode}
                currentAtBatPitches={atBatContext?.pitches ?? []}
                batterOnField={batterOnField}
                pitchingChange={incomingPitcher}
                atBatHits={atBatContext?.hits ?? []}
                lastAtBatResult={lastAtBatResult}
                dismissedAtBatIndex={dismissedAtBatIndex}
                resultReplayKey={replayCounter}
                onPlayerClick={onPlayerClickSeeded}
              />
              <div className={styles.replayPillSlot}>
                {lastAtBatResult && (
                  <button
                    type="button"
                    className={styles.replayPill}
                    onClick={handleReplayResult}
                    aria-label={`Replay at-bat result: ${lastAtBatResult.event}`}
                    data-testid="at-bat-replay-pill"
                  >
                    <span aria-hidden="true">↻</span> Replay {lastAtBatResult.event}
                  </button>
                )}
              </div>
            </div>
            <Lineup
              side="home"
              teamCode={ribbon.homeTeamCode}
              teamAbbr={ribbon.homeAbbr}
              hitters={homeLineup}
              onPlayerClick={onPlayerClickSeeded}
            />
          </section>
        )}
        <PlayerModal
          gamePk={gamePkNum}
          playerId={selectedPlayer?.id ?? null}
          seed={selectedPlayer}
          onClose={closeModal}
        />
        <InningDetailModal
          open={inningTarget !== null}
          target={inningTarget}
          snapshot={snapshot}
          statcast={statcast?.pitches}
          gamePk={gamePkNum}
          onClose={closeInningModal}
          onOpenPlayer={(seed) => {
            // Keep the inning modal mounted so closing PlayerModal returns
            // to it instead of the page. Both modals share --z-modal, so the
            // winner is decided by document order: both now portal to <body>
            // and PlayerModal mounts later, so it paints on top. (Before it
            // portalled, PlayerModal rendered inside <main> — i.e. *earlier*
            // than the inning modal's portal — and opened underneath it.)
            // The modal-stack ref guarantees only the topmost handles ESC.
            setSelectedPlayer(seed);
          }}
        />
      </main>
    </>
  );
}
